import { Grid, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { GridItemStyle, asteriskStyle } from './styled';

export type IDateRangeDataAutomation = {
  startDateInput?: string;
  endDateInput?: string;
  invalidDateMsg?: string;
};

export type DateRangePickerComponentProps = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  handleDateChange: (date: Dayjs | null, isStart?: boolean) => void;
  onError: (error: (string | null)[]) => void;
  error: (string | null)[];
  dataAutomation?: IDateRangeDataAutomation;
  minDate?: Dayjs;
  minDateErrorMsg?: string;
};

export const START_DATE_ERROR = "Start Date cannot be later than End Date";

export const END_DATE_ERROR = "End Date cannot be later than Current Date";

export const INVALID_DATE_ERROR = "Must enter a valid date";

export const START_DATE_IN_THE_FUTURE_ERROR = "Start Date cannot be in the future";

export const END_DATE_IN_THE_FUTURE_ERROR = "End Date cannot be in the future";

export const DateRangePickerComponent: React.FC<DateRangePickerComponentProps> = ({
  startDate,
  endDate,
  handleDateChange,
  onError,
  error,
  dataAutomation,
  minDate,
  minDateErrorMsg
}) => {
  const validateDate = (date: Dayjs | null, isStart: boolean) => {
    const currentDate = dayjs();
    if (!date?.isValid()) return INVALID_DATE_ERROR;
    if (minDate && date.isBefore(minDate, 'day')) return minDateErrorMsg ?? `Date should not be before ${minDate.format('MM/DD/YYYY')}`;
    if (date.isAfter(currentDate, 'day')) return isStart ? START_DATE_IN_THE_FUTURE_ERROR : END_DATE_IN_THE_FUTURE_ERROR;
    return null;
  };

  const validateDates = (start: Dayjs | null, end: Dayjs | null) => {
    const startDateError = validateDate(start, true);
    const endDateError = validateDate(end, false);
    if (start?.isValid() && end?.isValid() && start.isAfter(end)) {
      onError([START_DATE_ERROR, null]);
    } else {
      onError([startDateError, endDateError]);
    }
  };

  const handleDateChangeWrapper = (date: Dayjs | null, isStart: boolean) => {
    handleDateChange(date, isStart);
    if (isStart) {
      validateDates(date, endDate);
    } else {
      validateDates(startDate, date);
    }
  };

  const getHelperTextAutomationLocator = (isStart = true) => {
    const date = isStart ? startDate : endDate;
    return date?.isValid() ? dataAutomation?.invalidDateMsg : 'dateError';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid spacing={2} container sx={{ justifyContent: 'center' }}>
        <Grid item xs={12} md={4} sx={GridItemStyle}>
          <Typography data-automation="start-date-lbl">
            Start <Typography component={"span"} sx={asteriskStyle}>&nbsp;*</Typography>
          </Typography>
          <DatePicker
            label={error[0] ? 'Error' : ''}
            value={startDate}
            onChange={(date) => handleDateChangeWrapper(date, true)}
            slotProps={{
              textField: {
                helperText: (
                  <span data-automation={getHelperTextAutomationLocator()}>
                    {error[0]}
                  </span>
                ),
                inputProps: {
                  'data-automation': dataAutomation?.startDateInput ?? 'startDate'
                },
                error: !!error[0],
              },
            }}
            maxDate={endDate ?? undefined}
            minDate={minDate ?? undefined}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container sx={{ justifyContent: 'center' }}>
        <Grid item xs={12} md={4} sx={GridItemStyle}>
          <Typography data-automation="end-date-lbl">
            End <Typography component={"span"} sx={asteriskStyle}>&nbsp;*</Typography>
          </Typography>
          <DatePicker
            label={error[1] ? 'Error' : ''}
            value={endDate}
            onChange={(date) => handleDateChangeWrapper(date, false)}
            slotProps={{
              textField: {
                helperText: (
                  <span data-automation={getHelperTextAutomationLocator(false)}>
                    {error[1]}
                  </span>
                ),
                inputProps: {
                  'data-automation': dataAutomation?.endDateInput ?? 'endDate'
                },
                error: !!error[1]
              },
            }}
            maxDate={dayjs() ?? undefined}
            minDate={minDate ?? undefined}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};